import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useSubscription from 'hooks/payments/useSubscription';
import routes from 'common/routes';

// Se muestra una sola vez por login (se reinicia en logout, ver authSaga).
const SESSION_KEY = 'otx_grace_alert_seen';
const DANGER = '#B21616';

const whiteCtaSx = {
  py: 1.2,
  borderRadius: 2,
  fontWeight: 800,
  background: 'var(--otx-cta-bg)',
  color: 'var(--otx-cta-text)',
  '&:hover': { background: 'var(--otx-cta-bg)', filter: 'brightness(0.94)' },
  '&.Mui-disabled': { background: 'var(--otx-btn-disabled-bg)', color: 'var(--otx-btn-disabled-text)' },
};

const formatMoney = (amount, currency) => `$${Number(amount || 0).toLocaleString('es-CO')} ${currency || 'COP'}`;

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
};

/**
 * Gate de periodo de gracia: al iniciar sesión, si el cliente tiene una suscripción cuyo
 * cobro falló (estado grace_period), lanza un modal monocromo para reintentar el pago,
 * actualizar la tarjeta o cancelar. Se muestra una vez por login.
 */
function SubscriptionGraceGate() {
  const userId = useSelector((state) => state.auth.user?.id);
  const navigate = useNavigate();
  const { fetchMySubscription, retryPayment, cancelSubscription } = useSubscription(userId);

  const [sub, setSub] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // 'success' | 'failed' | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId || sessionStorage.getItem(SESSION_KEY)) return;
      try {
        const s = await fetchMySubscription();
        if (cancelled) return;
        if (s && s.status === 'grace_period') {
          setSub(s);
          setOpen(true);
          sessionStorage.setItem(SESSION_KEY, '1');
        }
      } catch {
        // silencioso: el gate es un extra, no debe romper el arranque
      }
    })();
    return () => { cancelled = true; };
  }, [userId, fetchMySubscription]);

  const close = useCallback(() => setOpen(false), []);

  const handleRetry = async () => {
    setBusy(true);
    setResult(null);
    try {
      const updated = await retryPayment(sub.id);
      setSub(updated);
      setResult(updated?.status === 'active' ? 'success' : 'failed');
    } catch {
      setResult('failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      await cancelSubscription(sub.id);
      close();
    } finally {
      setBusy(false);
    }
  };

  if (!sub) return null;

  const lastFour = sub.card_last4 ? `•••• ${sub.card_last4}` : 'tu tarjeta';
  const nextDate = formatDate(sub.next_charge_date);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main', color: 'text.primary' } }}
    >
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        {result === 'success' ? '¡Pago confirmado!' : 'No pudimos cobrar tu tarjeta'}
        <IconButton onClick={close} disabled={busy} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {result === 'success' ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: '#34d399' }} />
            <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Tu suscripción quedó al día. ¡Gracias!
            </Typography>
            <Button onClick={close} fullWidth sx={whiteCtaSx}>Continuar</Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start"
              sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(178,22,22,0.12)', border: '1px solid rgba(178,22,22,0.3)' }}>
              <ErrorOutlineIcon sx={{ color: '#ff8a8a', mt: 0.2 }} />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                No se pudo cobrar {formatMoney(sub.amount, sub.currency)} a {lastFour}.
                {nextDate ? ` Reintentaremos el ${nextDate}, pero ` : ' '}
                puedes reintentar ahora o actualizar tu tarjeta para no perder el acceso.
              </Typography>
            </Stack>

            {result === 'failed' && (
              <Typography variant="body2" sx={{ color: '#ff8a8a' }}>
                El reintento no fue aprobado. Prueba actualizando la tarjeta.
              </Typography>
            )}

            <Button onClick={handleRetry} disabled={busy} fullWidth sx={whiteCtaSx}>
              {busy ? <CircularProgress size={20} sx={{ color: 'var(--otx-cta-text)' }} /> : 'Reintentar pago'}
            </Button>

            <Button onClick={() => { close(); navigate(routes.listClientPayments.client.path); }} disabled={busy} variant="outlined" fullWidth
              sx={{ color: 'text.primary', borderColor: 'hairline.strong', fontWeight: 700, '&:hover': { borderColor: 'hairline.strong', background: 'var(--otx-surface-hover)' } }}>
              Actualizar tarjeta
            </Button>

            <Button onClick={handleCancel} disabled={busy} fullWidth
              sx={{ color: 'text.secondary', fontWeight: 700, transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), color 200ms cubic-bezier(0.32,0.72,0,1)', '&:hover': { backgroundColor: DANGER, color: 'text.primary' } }}>
              Cancelar suscripción
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SubscriptionGraceGate;
